import { Widget } from '@typeform/embed-react';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { ONE } from '@/constants';
import { useCurrentUser } from '@/hooks';
import { Alert, Snackbar, Typography } from '@/ui';
import { track } from '@/utils';

import { GetStartedContext } from '@/components/GetStarted/context';
import {
  useUpdateUserAssessmentMutation,
  useUpdateUserProfileMutation,
} from '@/store/api/userApi';
import { captureException } from '@/utils/errorMonitoring';

const MatchingForm = () => {
  const { user, isUserLoading, refetchUser } = useCurrentUser();
  const { nextStep } = useContext(GetStartedContext);
  const [updateUserAssessment] = useUpdateUserAssessmentMutation();
  const [updateUserProfile] = useUpdateUserProfileMutation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const completingRef = useRef(false);
  const matchPrefRef = useRef(false);
  const timezoneRef = useRef(false);

  useEffect(() => {
    track('ob_matching_form');
  }, []);

  const ensureAssessmentComplete = useCallback(
    async (surfaceError = false) => {
      if (!user.id) return;
      if (user.userAssesment?.complete) return;
      if (completingRef.current) return;

      completingRef.current = true;
      try {
        await updateUserAssessment({
          id: user.id,
          userAssesment: { complete: true },
        }).unwrap();
        await refetchUser();
      } catch (error) {
        captureException(error);
        if (surfaceError) {
          setErrorMessage(
            'We couldn’t mark onboarding as complete. You can continue, but you may be redirected back to Get Started until this saves.',
          );
        }
      } finally {
        // eslint-disable-next-line require-atomic-updates -- ref updates are intentional; we only use this as an in-flight guard
        completingRef.current = false;
      }
    },
    [refetchUser, updateUserAssessment, user.id, user.userAssesment?.complete],
  );

  useEffect(() => {
    if (isUserLoading || !user.id) return;
    ensureAssessmentComplete(false).catch(() => undefined);
  }, [ensureAssessmentComplete, isUserLoading, user.id]);

  useEffect(() => {
    if (isUserLoading || !user.id) return;
    if (user.profile?.matchPreference) return;
    if (matchPrefRef.current) return;
    matchPrefRef.current = true;

    const saveMatchPreference = async () => {
      try {
        await updateUserProfile({
          id: user.id,
          userProfile: { matchPreference: 'email' },
        }).unwrap();
        await refetchUser();
        track('ob_match_cont');
      } catch (error) {
        captureException(error);

        matchPrefRef.current = false;
      }
    };

    saveMatchPreference().catch(() => undefined);
  }, [isUserLoading, refetchUser, updateUserProfile, user.id]);

  // The coach matcher needs the member's timezone the moment this survey is submitted (MEN-5310).
  // Save the browser's zone now, before the survey, unless the member already has one; they can
  // still change it in their profile.
  useEffect(() => {
    if (isUserLoading || !user.id) return;
    if (user.profile?.timezone) return;
    if (timezoneRef.current) return;
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!browserTimezone) return;
    timezoneRef.current = true;

    const saveTimezone = async () => {
      try {
        await updateUserProfile({
          id: user.id,
          userProfile: { timezone: browserTimezone },
        }).unwrap();
        await refetchUser();
      } catch (error) {
        captureException(error);

        timezoneRef.current = false;
      }
    };

    saveTimezone().catch(() => undefined);
  }, [isUserLoading, refetchUser, updateUserProfile, user.id]);

  if (isUserLoading) return null;

  return (
    <main className="bg-gradient-dark-sunny flex flex-1 flex-col overflow-auto">
      <div className="container mx-auto flex flex-1 flex-col p-4">
        <Typography className="mb-5 text-center" variant="h4">
          Coach Matching
        </Typography>
        <Typography
          className="mx-auto mb-8 max-w-200 text-center"
          variant="body1"
        >
          Complete a 10 minute reflection to help us make a great match
        </Typography>
        <Widget
          className="rounded-lg shadow-lg"
          hidden={{ mentoId: user.id }}
          id="lhLlwrXQ"
          style={{ height: '650px', width: '100%' }}
          onSubmit={async () => {
            await ensureAssessmentComplete(true);
            nextStep(ONE);
          }}
        />
        <Typography className="mt-4 text-center" variant="body2">
          If your browser blocks third party tools you may not see a survey. In
          that case{' '}
          <a
            className="underline"
            href="https://mento-team.typeform.com/baseline"
            rel="noreferrer"
            target="_blank"
            onClick={async () => {
              await ensureAssessmentComplete(true);
              nextStep(ONE);
            }}
          >
            click this link
          </a>
          .
        </Typography>
      </div>
      <Snackbar
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        autoHideDuration={7000}
        open={Boolean(errorMessage)}
        onClose={() => setErrorMessage(null)}
      >
        <Alert
          severity="error"
          style={{ width: '100%' }}
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </main>
  );
};

export default MatchingForm;
